import React, {
  createContext,
  useContext,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import {useLocation} from 'react-router-dom';
import {getEnv} from '@/config/environment';
import {ROUTES} from '@/config/routesConstants';
import useModestExperimentOverrideShortcut from '@/hooks/use-modest-experiment-override-shortcut';
import useModestWebInitializer from '@/hooks/use-modest-web-initializer';
import {modestExperimentOverrideActions} from '@/stores/modestExperimentOverrideStore';

interface ModestWebConfig {
  appId: string;
  appName: string;
  appVersion: string;
}

interface ModestWebContextValue {
  config: ModestWebConfig;
  isLTXScaler: boolean;
  debugLogger: (...args: unknown[]) => void;
  modestWeb: any;
  modestInitialized: boolean;
  modestFetching: boolean;
}

const ModestWebContext = createContext<ModestWebContextValue | null>(null);

// Singleton instances of ModestWeb (one per appId)
const modestWebInstances = new Map<string, any>();
// Track initialization state per appId
const initializedApps = new Set<string>();

interface ModestWebProviderProps {
  children: ReactNode;
  debug?: boolean;
  enabled?: boolean;
}

export function ModestWebProvider({
  children,
  debug = false,
  enabled = true,
}: ModestWebProviderProps) {
  const location = useLocation();

  // Detect if we're in LTX Scaler based on the route
  const isLTXScaler = location.pathname.startsWith(ROUTES.CONTENT_LAB);

  const config = useMemo<ModestWebConfig>(() => {
    return isLTXScaler
      ? {
          appId: getEnv().VITE_MODEST_APP_ID_LTX_SCALER,
          appName: 'LTX Scaler',
          appVersion: '1.0.0',
        }
      : {
          appId: getEnv().VITE_MODEST_APP_ID_BRANDS,
          appName: 'Brands',
          appVersion: '1.0.0',
        };
  }, [isLTXScaler]);

  const debugLogger = useCallback(
    (...args: unknown[]) => {
      if (debug) {
        console.log(...args); // eslint-disable-line no-console
      }
    },
    [debug]
  );

  // Check if we already have an initialized instance for this app
  const existingInstance = modestWebInstances.get(config.appId);
  const isAlreadyInitialized = initializedApps.has(config.appId);

  // Only call the initializer if we don't have an instance yet
  const shouldInitialize = !existingInstance && !isAlreadyInitialized;

  // Call the initializer to get ModestWeb state
  const {modestWeb, modestInitialized, modestFetching, dashboardUser} =
    useModestWebInitializer({
      appId: config.appId,
      appName: config.appName,
      appVersion: config.appVersion,
      env: getEnv().MODE === 'production' ? 'prod' : 'dev',
      debugLogger,
      enabled,
    });

  // Store the instance when it's ready (only if enabled)
  useEffect(() => {
    if (enabled && modestWeb && modestInitialized && !existingInstance) {
      modestWebInstances.set(config.appId, modestWeb);
      initializedApps.add(config.appId);
      debugLogger('ModestWeb: Stored instance in context for', {
        appId: config.appId,
        appName: config.appName,
      });
    }
  }, [
    enabled,
    modestWeb,
    modestInitialized,
    existingInstance,
    config.appId,
    config.appName,
    debugLogger,
  ]);

  // Use existing instance if available, otherwise use the one from initializer
  // When disabled, return null for modestWeb but keep initialized true to not block app loading
  const finalModestWeb = enabled ? existingInstance || modestWeb : null;
  const finalModestInitialized = isAlreadyInitialized || modestInitialized;
  const finalModestFetching = shouldInitialize ? modestFetching : false;

  // Initialize developer override system
  useModestExperimentOverrideShortcut();

  // Enable developer overrides for platform admins in all environments
  useEffect(() => {
    // Only proceed if dashboardUser is loaded
    if (!dashboardUser) {
      modestExperimentOverrideActions.setIsEnabled(false);
      return;
    }

    const isPlatformAdmin = dashboardUser.hasPlatformAdminRole;

    if (isPlatformAdmin) {
      modestExperimentOverrideActions.setIsEnabled(true);
      debugLogger(
        'ModestWeb Provider: Developer overrides enabled for platform admin'
      );
    } else {
      modestExperimentOverrideActions.setIsEnabled(false);
      debugLogger(
        'ModestWeb Provider: Developer overrides disabled - user is not a platform admin'
      );
    }
  }, [dashboardUser, dashboardUser?.hasPlatformAdminRole, debugLogger]);

  const value = useMemo<ModestWebContextValue>(() => {
    return {
      config,
      isLTXScaler,
      debugLogger,
      modestWeb: finalModestWeb,
      modestInitialized: finalModestInitialized,
      modestFetching: finalModestFetching,
    };
  }, [
    config,
    isLTXScaler,
    debugLogger,
    finalModestWeb,
    finalModestInitialized,
    finalModestFetching,
  ]);

  return (
    <ModestWebContext.Provider value={value}>
      {children}
    </ModestWebContext.Provider>
  );
}

export function useModestWebContext(): ModestWebContextValue {
  const context = useContext(ModestWebContext);
  if (!context) {
    throw new Error(
      'useModestWebContext must be used within a ModestWebProvider'
    );
  }
  return context;
}

export default ModestWebProvider;
